import { useClientConfig } from "@/features/client-config/contexts/client-config/use-client-config";
import { configManager } from "@/features/client-config/services/config-manager";
import { useDeviceData } from "@/features/setup/hooks/use-device-data";
import { globalWebSocketManager } from "@/features/shot-tracking/services/global-websocket-manager";
import { useAuth } from "@/hooks/use-auth";
import { calculateBackoffMs } from "@/utils/backoff";
import { useEffect } from "react";

import { radarFetchStateStore } from "../services/radar-fetch-state-store";
import { systemConfigService } from "../services/system-config-service";
import { mergeFetchedSystemConfigs } from "../utils/merge-fetched-system-configs";
import { rebindRadarIds } from "../utils/rebind-radar-ids";

const RADAR_FETCH_BASE_BACKOFF_MS = 5_000;
const RADAR_FETCH_MAX_BACKOFF_MS = 60_000;

function isReadyForRetry(radarId: string): boolean {
  const { fetchState, attemptCount, lastAttemptAt } =
    radarFetchStateStore.getStatus(radarId);
  if (fetchState !== "failed") {
    return true;
  }
  if (!lastAttemptAt) {
    return true;
  }
  const backoffMs = calculateBackoffMs(attemptCount, {
    baseMs: RADAR_FETCH_BASE_BACKOFF_MS,
    maxMs: RADAR_FETCH_MAX_BACKOFF_MS,
  });
  return Date.now() - lastAttemptAt >= backoffMs;
}

export const useInitializeWebSocket = () => {
  const { clientConfig, refreshConfig } = useClientConfig();
  const { isAuthenticated } = useAuth();
  const { data: deviceData, loading: isLoadingDeviceData } = useDeviceData();
  const clientId = clientConfig?.clientId;
  const radars = clientConfig?.radars;
  const enabledRadars = radars?.filter((radar) => radar.enabled);
  const radarsMissingSystemConfig = enabledRadars?.filter(
    (radar) => !radar.systemConfig
  );
  const radarsReadyToFetch = radarsMissingSystemConfig?.filter((radar) =>
    isReadyForRetry(radar.id)
  );
  const hasRadarsReadyToFetch = (radarsReadyToFetch?.length ?? 0) > 0;

  const cloudRadars =
    deviceData?.applicationData?.__typename === "OnCourseKioskDeviceData"
      ? deviceData.applicationData.device?.radars
      : undefined;

  // Rebinds fielded devices' locally-generated radar ids to stable cloud ids.
  // See .agents/plans/phase-1-stable-identity.md.
  useEffect(() => {
    if (
      !isAuthenticated ||
      isLoadingDeviceData ||
      !cloudRadars ||
      !clientConfig ||
      !radars
    ) {
      return;
    }

    const {
      radars: rebound,
      rebound: reboundCount,
      defaultRadarId,
    } = rebindRadarIds(radars, cloudRadars, clientConfig.defaultRadarId);

    if (reboundCount === 0) {
      return;
    }

    const rebindRadars = async () => {
      await configManager.setConfig({
        ...clientConfig,
        radars: rebound,
        defaultRadarId,
      });
      refreshConfig();
    };

    rebindRadars();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per radar-set/cloud-data change, not on every clientConfig identity change
  }, [isAuthenticated, isLoadingDeviceData, cloudRadars, radars]);

  // Keep the manager's cached config current so the hot shot-ingestion path
  // doesn't hit storage on every websocket frame (see setClientConfig doc).
  useEffect(() => {
    globalWebSocketManager.setClientConfig(clientConfig ?? null);
  }, [clientConfig]);

  // Initialize and refresh WebSocket when client config becomes available
  const radarIdsAndEnabledState = JSON.stringify(
    radars?.map((radar) => ({ id: radar.id, enabled: radar.enabled }))
  );
  useEffect(() => {
    if (!clientId || !enabledRadars) {
      return;
    }
    globalWebSocketManager.initialize(enabledRadars).then((results) => {
      const failedRadarIds = [...(results ?? [])]
        .filter(([, success]) => !success)
        .map(([radarId]) => radarId);
      if (failedRadarIds.length > 0) {
        console.error(
          `WebSocket connection failed for radars: ${failedRadarIds.join(", ")}`
        );
      }
    });
    globalWebSocketManager.refreshClientId();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-init only when the set/enabled-state of radars actually changes
  }, [clientId, radarIdsAndEnabledState]);

  // Fetch radar system config for any radar that doesn't have one yet,
  // retrying failures with backoff while the radar stays enabled.
  useEffect(() => {
    if (
      !isAuthenticated ||
      !clientId ||
      !clientConfig ||
      !hasRadarsReadyToFetch
    ) {
      return;
    }

    const fetchRadarConfigs = async () => {
      const fetchedByRadarId = new Map(
        await Promise.all(
          (radarsReadyToFetch ?? []).map(async (radar) => {
            radarFetchStateStore.setFetching(radar.id);
            const result = await systemConfigService.fetchSystemConfig(
              radar.radarHttpBaseUrl,
              radar.id
            );
            if (result.status === "ok" || result.status === "not-modified") {
              radarFetchStateStore.setOk(radar.id);
            } else if (result.status === "no-url") {
              radarFetchStateStore.setNoUrl(radar.id);
            } else {
              radarFetchStateStore.setFailed(radar.id, result.error);
            }
            return [radar.id, result] as const;
          })
        )
      );

      if (
        [...fetchedByRadarId.values()].every((result) => result.status !== "ok")
      ) {
        return;
      }

      const currentConfig = await configManager.getConfig();
      if (!currentConfig) {
        return;
      }

      await configManager.setConfig(
        mergeFetchedSystemConfigs(currentConfig, fetchedByRadarId)
      );
      refreshConfig();
    };

    fetchRadarConfigs().catch((error) => {
      console.error("Failed to fetch radar configs:", error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshConfig is called for side effect only
  }, [isAuthenticated, clientId, hasRadarsReadyToFetch, clientConfig]);

  // Re-run the fetch effect once a failed radar's backoff window elapses,
  // since the fetch-state store is external to React and its changes don't
  // otherwise trigger a re-render.
  useEffect(() => {
    const pendingRadars = radarsMissingSystemConfig?.filter(
      (radar) =>
        radarFetchStateStore.getStatus(radar.id).fetchState === "failed"
    );
    if (!pendingRadars || pendingRadars.length === 0) {
      return;
    }

    const timers = pendingRadars.map((radar) => {
      const { attemptCount, lastAttemptAt } = radarFetchStateStore.getStatus(
        radar.id
      );
      const backoffMs = calculateBackoffMs(attemptCount, {
        baseMs: RADAR_FETCH_BASE_BACKOFF_MS,
        maxMs: RADAR_FETCH_MAX_BACKOFF_MS,
      });
      const remainingMs = Math.max(
        0,
        backoffMs - (Date.now() - (lastAttemptAt ?? Date.now()))
      );
      return setTimeout(() => refreshConfig(), remainingMs);
    });

    return () => {
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshConfig is called for side effect only, timers keyed on which radars are missing config
  }, [radarsMissingSystemConfig]);
};
